import { Button } from '@mantine/core';
import { useLocation, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ColorInputSize, ColorType } from '../../../types/ColorPicker';
import { extractThemeColors } from '../../../utils/extractThemeColors';
import { isGradientColor } from '../../../utils/isGradientColor';
import { searchObjectToParams } from '../../../utils/query.utils';
import { useGlobalTheme } from '../../../utils/useGlobalTheme';
import { useWorkMode } from '../../../utils/useWorkMode';
import { ColorInputPopover } from '../../inputs/ColorInputPopover';
import { useLearningObjectCommonData } from '../../providers/LearningObjectCommonDataProvider';

type ThemeColorInputProps = {
  color: string;
  onSelectColor: ({ color, type }: { color: string; type: ColorType }) => void;
  originalColor?: string;
  themeColors?: string[];
  size?: ColorInputSize;
  withGradient?: boolean;
  onDelete?: () => void;
  dataTestId?: string;
};

export function ThemeColorInput(props: ThemeColorInputProps) {
  const {
    color,
    originalColor,
    onSelectColor,
    themeColors,
    withGradient = false,
    size = ColorInputSize.large,
    onDelete,
    dataTestId
  } = props;

  const theme = useGlobalTheme();
  const finalThemeColors = themeColors ?? extractThemeColors({ colors: theme.other.colors });

  const originalGrdientColorType =
    originalColor && isGradientColor(originalColor) ? ColorType.gradient : ColorType.solid;

  const handleReset = () => {
    onSelectColor({ color: originalColor ?? '', type: originalGrdientColorType });
  };

  const onReset = originalColor && originalColor !== color ? handleReset : undefined;

  const EditThemeColors = () => {
    const { pathname } = useLocation();
    const search = useSearch({ strict: false });
    const { isStudioEdit } = useWorkMode();
    const { t } = useTranslation('components.colorPicker');
    const { themeId } = useLearningObjectCommonData();

    const handleEdit = () => {
      if (themeId) {
        const params = search ? searchObjectToParams(search) : new URLSearchParams();
        params.set('themeId', themeId);
        window.history.pushState(null, '', `${pathname}?${params.toString()}`);
      }
    };

    if (!isStudioEdit || !themeId) {
      return null;
    }
    return (
      <Button variant="transparent" onClick={handleEdit}>
        {t('edit')}
      </Button>
    );
  };

  return (
    <ColorInputPopover
      themeColors={finalThemeColors}
      color={color}
      onSelectColor={onSelectColor}
      withGradient={withGradient}
      size={size}
      onDelete={onDelete}
      onReset={onReset}
      editComponent={<EditThemeColors />}
      dataTestId={dataTestId}
    />
  );
}
